import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { Switch } from '@multaqa/ui/components/ui/switch'
import { XIcon } from 'lucide-react'
import { useState } from 'react'
import { formatErrorMessage } from '@/lib/error-message'
import type { GroupEntitlementRow } from './api'
import { useDeleteEntitlement, useUpsertEntitlement } from './api'

/**
 * One labelled row per `module.*` switch — the half of the entitlements tab
 * that grants or denies a whole module. Toggling the switch writes an
 * override directly (plans/58 D-17 kept the two-step edit/save workflow for
 * this; the module tab drops it because there is only one value a switch can
 * hold). Clearing an override — the way back to whatever the plan says —
 * still confirms first: it can disable a module a live customer uses today.
 */
export function GroupModules({ groupId, rows }: { groupId: string; rows: GroupEntitlementRow[] }) {
  const { t } = useTranslation()
  const upsert = useUpsertEntitlement()
  const [clearing, setClearing] = useState<GroupEntitlementRow | null>(null)

  return (
    <section aria-labelledby="group-modules-heading" data-testid="group-modules-panel">
      <h2 id="group-modules-heading" className="text-h3 mb-3">
        {t('groups.detail.modules.title')}
      </h2>
      {rows.length === 0 ? (
        <p className="text-small text-muted-foreground">{t('groups.detail.modules.empty')}</p>
      ) : (
        <ul
          className="divide-y divide-border border border-border"
          data-testid="group-modules-list"
        >
          {rows.map((row) => {
            const slug = moduleSlug(row.key)
            const label = t(`module.names.${slug}`)
            const overridden = row.override_value !== null

            return (
              <li
                key={row.key}
                className="flex items-center justify-between gap-3 p-3"
                data-testid={`group-module-row-${row.key}`}
              >
                <div className="grid gap-0.5">
                  <span className="text-body">{label}</span>
                  <span
                    className="text-small text-muted-foreground"
                    data-testid={`group-module-source-${row.key}`}
                  >
                    {overridden
                      ? t('groups.detail.entitlementsSource.override')
                      : t('groups.detail.entitlementsSource.plan')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {overridden ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setClearing(row)}
                      aria-label={t('groups.detail.modules.clear', { module: label })}
                      data-testid={`group-module-clear-${row.key}`}
                    >
                      <XIcon className="size-3.5" aria-hidden="true" />
                    </Button>
                  ) : null}
                  <Switch
                    checked={isGranted(row.effective_value)}
                    disabled={upsert.isPending}
                    onCheckedChange={(checked) =>
                      upsert.mutate(
                        { groupId, key: row.key, value: checked ? 'true' : 'false' },
                        { onError: (error) => toast.error(formatErrorMessage(error)) },
                      )
                    }
                    aria-label={label}
                    data-testid={`group-module-switch-${row.key}`}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      )}
      {clearing ? (
        <ClearModuleConfirm groupId={groupId} row={clearing} onClose={() => setClearing(null)} />
      ) : null}
    </section>
  )
}

function ClearModuleConfirm({
  groupId,
  row,
  onClose,
}: {
  groupId: string
  row: GroupEntitlementRow
  onClose: () => void
}) {
  const { t } = useTranslation()
  const remove = useDeleteEntitlement()
  const label = t(`module.names.${moduleSlug(row.key)}`)

  return (
    <Dialog open onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent closeLabel={t('action.cancel')} data-testid="group-module-clear-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('groups.detail.modules.clearDialog.title', { module: label })}
          </DialogTitle>
          <DialogDescription>
            {t('groups.detail.modules.clearDialog.description', { module: label })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter showCloseButton={false} closeLabel={t('action.cancel')}>
          <Button variant="outline" onClick={onClose} disabled={remove.isPending}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="destructive"
            loading={remove.isPending}
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate(
                { groupId, key: row.key },
                {
                  onSuccess: () => {
                    toast.success(t('groups.detail.modules.clearDialog.success', { module: label }))
                    onClose()
                  },
                  onError: (error) => toast.error(formatErrorMessage(error)),
                },
              )
            }
            data-testid="group-module-clear-confirm"
          >
            {t('groups.detail.modules.clearDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function moduleSlug(key: string): string {
  return key.replace(/^module\./, '')
}

export function isGranted(value: string | null | undefined): boolean {
  if (value === null || value === undefined) return false
  const v = value.trim().toLowerCase()
  return v !== '' && v !== '0' && v !== 'false' && v !== 'no'
}
